import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ArrowUp, ArrowDown } from 'lucide-react';
import { getTokenPayload } from '../../utils';
import { getSubAdminStatusesAdmin } from '../Common/actions';

const ALLOWED_EMAILS = [
  'vijay.kumar@ambalaproductions.in',
  'harjaspreet.singh@ambalaproductions.in'
];

const POLL_INTERVAL = 90000;
const PANEL_WIDTH = 360;

// --- Formatting helpers ---

function formatOfflineDuration(value) {
  if (!value || String(value).trim() === '') return '';
  const timestamp = Number(value);
  if (isNaN(timestamp)) return '';
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'less than a minute';
  if (mins === 1) return '1 minute';
  if (mins < 60) return `${mins} minutes`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) {
    if (remMins === 0) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    return `${hours}h ${remMins}m`;
  }
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  const months = Math.floor(days / 30);
  return `${months} ${months === 1 ? 'month' : 'months'}`;
}

function formatLastLogin(value) {
  if (!value || String(value).trim() === '') return 'Never';
  const timestamp = Number(value);
  if (isNaN(timestamp)) return 'Never';
  const date = new Date(timestamp);
  const now = new Date();
  const hours = date.getHours();
  const mins = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h = hours % 12 || 12;
  const time = `${h}:${mins} ${ampm}`;
  const isToday = date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();
  if (isToday) return `Today, ${time}`;
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}, ${time}`;
}

function isTimestampToday(value) {
  if (!value || String(value).trim() === '') return false;
  const ts = Number(value);
  if (isNaN(ts)) return false;
  const d = new Date(ts);
  const now = new Date();
  return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

function isOfflineMoreThanADay(value) {
  if (!value || String(value).trim() === '') return false;
  const ts = Number(value);
  if (isNaN(ts)) return false;
  return Date.now() - ts > 86400000;
}

function getAccentColor(admin) {
  if (admin.isOffline === 'no') return '#22C55E';
  const lastActiveTs = admin.lastActivityOn || admin.lastLoginOn;
  const offlineLong = isOfflineMoreThanADay(lastActiveTs);
  if (offlineLong) return '#EF4444';
  return isTimestampToday(admin.lastLoginOn) ? '#F59E0B' : '#EF4444';
}

// --- Styles ---

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1299,
  },
  panel: (isOpen) => ({
    position: 'fixed',
    top: 0,
    right: 0,
    width: PANEL_WIDTH,
    height: '100vh',
    background: '#1A1D27',
    borderLeft: '1px solid #2E3247',
    zIndex: 1300,
    transform: isOpen ? 'translateX(0)' : `translateX(${PANEL_WIDTH}px)`,
    transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: isOpen ? '-8px 0 24px rgba(0,0,0,0.4)' : 'none',
  }),
  edgeButton: (top, isOpen, isDragging) => ({
    position: 'fixed',
    right: isOpen ? PANEL_WIDTH : 0,
    top,
    padding: '8px 12px',
    background: 'rgba(239, 68, 68, 0.75)',
    border: 'none',
    borderRadius: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    cursor: isDragging ? 'grabbing' : 'pointer',
    opacity: 1,
    transition: isDragging ? 'none' : 'right 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1301,
    userSelect: 'none',
    boxShadow: '0 2px 10px rgba(239,68,68,0.4)',
  }),
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #2E3247',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  headerTitle: {
    color: '#F1F5F9',
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    color: '#8B92A5',
    cursor: 'pointer',
    padding: 6,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 200ms, background 200ms',
  },
  lastUpdated: {
    color: '#4A5068',
    fontSize: '0.7rem',
    padding: '4px 20px 0',
    flexShrink: 0,
  },
  cardList: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
  },
  card: (accentColor, index) => ({
    background: '#141620',
    borderRadius: 8,
    borderLeft: `3px solid ${accentColor}`,
    padding: '8px 12px',
    marginBottom: 6,
    animation: `fadeInUp 300ms ${index * 60}ms both`,
  }),
  cardTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardName: {
    color: '#F1F5F9',
    fontSize: '0.85rem',
    fontWeight: 600,
    margin: 0,
  },
  cardEmail: {
    color: '#6B7280',
    fontSize: '0.68rem',
    margin: '1px 0 5px',
  },
  statusDot: (online) => ({
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: online ? '#22C55E' : '#EF4444',
    boxShadow: online ? '0 0 6px rgba(34,197,94,0.4)' : '0 0 6px rgba(239,68,68,0.4)',
    flexShrink: 0,
  }),
  statusText: (online) => ({
    color: online ? '#22C55E' : '#EF4444',
    fontSize: '0.75rem',
    fontWeight: 500,
  }),
  detailLabel: {
    color: '#6B7280',
    fontSize: '0.73rem',
  },
  detailValue: {
    color: '#94A3B8',
    fontSize: '0.73rem',
  },
  edgeButtonText: {
    color: '#FFFFFF',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
  },
};

const keyframesStyle = `
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
`;

export default function AdminActivityPanel() {
  const [allowed, setAllowed] = useState(null); // null = checking, true/false = resolved
  const [admins, setAdmins] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buttonTop, setButtonTop] = useState(() => Math.round(window.innerHeight * 0.25));
  const [isDragging, setIsDragging] = useState(false);

  const pollRef = useRef(null);
  const dragStartY = useRef(0);
  const dragStartTop = useRef(0);
  const hasDragged = useRef(false);
  const mySubAdminId = useRef(null);

  const fetchStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getSubAdminStatusesAdmin();
      const list = res?.body || [];
      setLastUpdated(new Date());

      const payload = getTokenPayload();
      const myId = payload?.subAdminId;
      mySubAdminId.current = myId;

      // Filter out self and inactive sub-admins
      const filtered = list.filter(a => a.subAdminId !== myId && a.inService !== 'no');

      // Sort alphabetically by name
      filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      setAdmins(filtered);
      return { list, myId };
    } catch (err) {
      console.error('Failed to fetch admin statuses:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial access check
  useEffect(() => {
    fetchStatuses().then((result) => {
      if (!result) { setAllowed(false); return; }
      const { list, myId } = result;
      const myEntry = list.find(a => a.subAdminId === myId);
      if (myEntry && ALLOWED_EMAILS.includes(myEntry.email)) {
        setAllowed(true);
      } else {
        setAllowed(false);
      }
    });
  }, [fetchStatuses]);

  // Polling when panel is open
  useEffect(() => {
    if (isOpen && allowed) {
      fetchStatuses();
      pollRef.current = setInterval(fetchStatuses, POLL_INTERVAL);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isOpen, allowed, fetchStatuses]);

  // Drag handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    hasDragged.current = false;
    dragStartY.current = e.clientY;
    dragStartTop.current = buttonTop;
  }, [buttonTop]);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    hasDragged.current = false;
    dragStartY.current = touch.clientY;
    dragStartTop.current = buttonTop;
  }, [buttonTop]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (clientY) => {
      const delta = clientY - dragStartY.current;
      if (Math.abs(delta) > 3) hasDragged.current = true;
      const newTop = Math.max(10, Math.min(window.innerHeight - 100, dragStartTop.current + delta));
      setButtonTop(newTop);
    };

    const onMouseMove = (e) => handleMove(e.clientY);
    const onTouchMove = (e) => handleMove(e.touches[0].clientY);

    const onEnd = () => setIsDragging(false);

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('touchend', onEnd);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [isDragging]);

  const handleButtonClick = () => {
    if (hasDragged.current) return;
    setIsOpen(prev => !prev);
  };

  if (allowed === null || allowed === false) return null;

  return (
    <>
      <style>{keyframesStyle}</style>

      {/* Click-outside overlay */}
      {isOpen && (
        <div
          style={styles.overlay}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Edge button */}
      <div
        style={styles.edgeButton(buttonTop, isOpen, isDragging)}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onClick={handleButtonClick}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={styles.edgeButtonText}>Team Status</span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.55rem', fontWeight: 400 }}>{isOpen ? 'click to close' : 'click to open'}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <ArrowUp size={12} color="#FFFFFF" style={{ opacity: 0.7 }} />
          <ArrowDown size={12} color="#FFFFFF" style={{ opacity: 0.7 }} />
        </div>
      </div>

      {/* Side panel */}
      <div style={styles.panel(isOpen)}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.headerTitle}>Team Work Status</h3>
          <div style={styles.headerRight}>
            <button
              style={styles.iconBtn}
              onClick={() => setIsOpen(false)}
              title="Close"
              onMouseEnter={(e) => { e.currentTarget.style.color = '#F1F5F9'; e.currentTarget.style.background = 'rgba(108,99,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#8B92A5'; e.currentTarget.style.background = 'none'; }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Admin cards */}
        <div
          style={styles.cardList}
          className="admin-activity-scroll"
        >
          <style>{`
            .admin-activity-scroll::-webkit-scrollbar { width: 3px; }
            .admin-activity-scroll::-webkit-scrollbar-track { background: transparent; }
            .admin-activity-scroll::-webkit-scrollbar-thumb { background: #2E3247; border-radius: 3px; }
          `}</style>
          {admins.map((admin, i) => {
            const online = admin.isOffline === 'no';
            const accent = getAccentColor(admin);
            return (
              <div key={admin.subAdminId} style={styles.card(accent, i)}>
                <div style={styles.cardTopRow}>
                  <p style={styles.cardName}>{admin.name || 'Unknown'}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={styles.statusDot(online)} />
                    <span style={styles.statusText(online)}>
                      {online ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
                <p style={styles.cardEmail}>{admin.email}</p>
                {!online && formatOfflineDuration(admin.lastActivityOn || admin.lastLoginOn) !== '' && (
                  <div style={{ marginBottom: 0 }}>
                    <span style={styles.detailLabel}>Offline since: </span>
                    <span style={{
                      ...styles.detailValue,
                      ...(isTimestampToday(admin.lastLoginOn) ? {
                        color: '#F59E0B',
                        fontWeight: 600,
                      } : {}),
                    }}>{formatOfflineDuration(admin.lastActivityOn || admin.lastLoginOn)}</span>
                  </div>
                )}
                <div style={{ marginTop: -2 }}>
                  <span style={styles.detailLabel}>Last Login: </span>
                  <span style={styles.detailValue}>{formatLastLogin(admin.lastLoginOn)}</span>
                </div>
              </div>
            );
          })}
          {admins.length === 0 && !loading && (
            <div style={{ color: '#4A5068', textAlign: 'center', padding: '40px 0', fontSize: '0.85rem' }}>
              No admin statuses available
            </div>
          )}
        </div>
      </div>
    </>
  );
}
